import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { 
  CreditCard, Plus, Trash2, Star, Loader2, CheckCircle, AlertCircle,
  Building2, Receipt, Settings, RefreshCw, Download, ExternalLink,
  Bell, Calendar, Shield
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Card brand icons/colors
const cardBrandColors = {
  visa: 'bg-blue-600',
  mastercard: 'bg-red-600',
  amex: 'bg-blue-800',
  discover: 'bg-orange-500',
  default: 'bg-gray-600'
};

// Load Stripe.js dynamically
const loadStripe = (publishableKey) => {
  return new Promise((resolve) => {
    if (window.Stripe) {
      resolve(window.Stripe(publishableKey));
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => {
      resolve(window.Stripe(publishableKey));
    };
    document.head.appendChild(script);
  });
};

const PaymentMethodsSettings = () => {
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [billingInfo, setBillingInfo] = useState({});
  const [autoRenewal, setAutoRenewal] = useState({ enabled: true, notify_days_before: 7 });
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [publishableKey, setPublishableKey] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  
  // Dialog states
  const [addCardDialogOpen, setAddCardDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  
  // Form states
  const [addingCard, setAddingCard] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);
  const [savingAutoRenewal, setSavingAutoRenewal] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(true);
  const [configMessage, setConfigMessage] = useState('');
  
  // Stripe Elements state
  const [stripe, setStripe] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const cardElementRef = useRef(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchPaymentMethods(),
      fetchBillingInfo(),
      fetchAutoRenewalSettings(),
      fetchPaymentHistory()
    ]);
    setLoading(false);
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/clinic-payment-methods/list`);
      setPaymentMethods(response.data.payment_methods || []);
      setPublishableKey(response.data.publishable_key);
      setStripeConfigured(response.data.stripe_configured !== false);
      setConfigMessage(response.data.message || '');
      setDemoMode(false); // No more demo mode
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
    }
  };

  const fetchBillingInfo = async () => {
    try {
      const response = await axios.get(`${API}/clinic-payment-methods/billing-info`);
      setBillingInfo(response.data || {});
    } catch (error) {
      console.error('Failed to fetch billing info:', error);
    }
  };

  const fetchAutoRenewalSettings = async () => {
    try {
      const response = await axios.get(`${API}/clinic-payment-methods/auto-renewal`);
      setAutoRenewal(response.data || { enabled: true, notify_days_before: 7 });
    } catch (error) {
      console.error('Failed to fetch auto-renewal settings:', error);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const response = await axios.get(`${API}/clinic-payment-methods/payment-history`);
      setPaymentHistory(response.data.payments || []);
    } catch (error) {
      console.error('Failed to fetch payment history:', error);
    }
  };

  const handleAddCard = async () => {
    if (!stripeConfigured) {
      toast.error('Please configure Stripe in Settings > Billing first');
      return;
    }
    
    setAddingCard(true);
    setCardError(null);
    
    try {
      // Get SetupIntent from backend
      const setupResponse = await axios.post(`${API}/clinic-payment-methods/setup-intent`);
      
      if (setupResponse.data.client_secret && setupResponse.data.publishable_key) {
        setClientSecret(setupResponse.data.client_secret);
        
        // Load Stripe.js and create Elements
        const stripeInstance = await loadStripe(setupResponse.data.publishable_key);
        setStripe(stripeInstance);
        
        // Open dialog first, then mount elements after a small delay
        setAddCardDialogOpen(true);
        
        setTimeout(() => {
          if (cardElementRef.current && stripeInstance) {
            const elements = stripeInstance.elements();
            const card = elements.create('card', {
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  fontFamily: 'system-ui, sans-serif',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#e53e3e',
                  iconColor: '#e53e3e',
                },
              },
            });
            card.mount(cardElementRef.current);
            card.on('change', (event) => {
              setCardError(event.error ? event.error.message : null);
              setCardComplete(event.complete);
            });
            setCardElement(card);
          }
        }, 100);
      } else {
        toast.error(setupResponse.data.detail || 'Failed to initialize card setup');
      }
    } catch (error) {
      console.error('Setup intent error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initialize. Please ensure Stripe is configured in Billing settings.');
    } finally {
      setAddingCard(false);
    }
  };
  
  const handleConfirmAddCard = async () => {
    if (!stripe || !cardElement || !clientSecret) {
      toast.error('Card form not ready. Please try again.');
      return;
    }
    
    if (!cardComplete) {
      toast.error('Please enter complete card details');
      return;
    }
    
    setAddingCard(true);
    
    try {
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });
      
      if (error) {
        setCardError(error.message);
        toast.error(error.message);
      } else if (setupIntent.status === 'succeeded') {
        // Card was successfully added, now register it in our backend
        await axios.post(`${API}/clinic-payment-methods/add`, {
          payment_method_id: setupIntent.payment_method,
          set_as_default: true
        });
        
        toast.success('Card added successfully!');
        setAddCardDialogOpen(false);
        setClientSecret(null);
        setCardElement(null);
        setCardComplete(false);
        await fetchPaymentMethods();
      }
    } catch (error) {
      console.error('Confirm card error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add card');
    } finally {
      setAddingCard(false);
    }
  };
  
  const handleCloseAddCardDialog = () => {
    if (cardElement) {
      cardElement.destroy();
      setCardElement(null);
    }
    setAddCardDialogOpen(false);
    setClientSecret(null);
    setCardError(null);
    setCardComplete(false);
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      await axios.post(`${API}/clinic-payment-methods/set-default/${paymentMethodId}`);
      toast.success('Default payment method updated');
      await fetchPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update default');
    }
  };

  const handleDeleteCard = async () => {
    if (!selectedCard) return;
    
    try {
      await axios.delete(`${API}/clinic-payment-methods/${selectedCard.id}`);
      toast.success('Card removed');
      setDeleteDialogOpen(false);
      setSelectedCard(null);
      await fetchPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove card');
    }
  };

  const handleSaveBillingInfo = async () => {
    setSavingBilling(true);
    try {
      await axios.post(`${API}/clinic-payment-methods/billing-info`, billingInfo);
      toast.success('Billing information saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save billing info');
    } finally {
      setSavingBilling(false);
    }
  };

  const handleSaveAutoRenewal = async () => {
    setSavingAutoRenewal(true);
    try {
      await axios.post(`${API}/clinic-payment-methods/auto-renewal`, autoRenewal);
      toast.success('Auto-renewal settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingAutoRenewal(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Platform Stripe Not Configured Notice - Should not normally appear */}
      {!stripeConfigured && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Payment System Unavailable:</strong> {configMessage || 'Please contact support for assistance.'}
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="cards" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="cards">
            <CreditCard className="h-4 w-4 mr-2" />
            Payment Methods
          </TabsTrigger>
          <TabsTrigger value="billing">
            <Building2 className="h-4 w-4 mr-2" />
            Billing Info
          </TabsTrigger>
          <TabsTrigger value="history">
            <Receipt className="h-4 w-4 mr-2" />
            Payment History
          </TabsTrigger>
          <TabsTrigger value="auto-renewal">
            <RefreshCw className="h-4 w-4 mr-2" />
            Auto-Renewal
          </TabsTrigger>
        </TabsList>

        {/* Payment Methods Tab */}
        <TabsContent value="cards">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Saved Payment Methods</CardTitle>
                  <CardDescription>
                    Manage your cards for subscription payments
                  </CardDescription>
                </div>
                <Button onClick={() => setAddCardDialogOpen(true)} disabled={!stripeConfigured}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Card
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {paymentMethods.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CreditCard className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">No payment methods saved</p>
                  <p className="text-sm mt-1">Add a card to enable easy payments and auto-renewal</p>
                  <Button className="mt-4" onClick={() => setAddCardDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Card
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {paymentMethods.map((pm) => (
                    <div 
                      key={pm.id}
                      className={`flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                        pm.is_default ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-8 rounded flex items-center justify-center text-white text-xs font-bold ${
                          cardBrandColors[pm.brand?.toLowerCase()] || cardBrandColors.default
                        }`}>
                          {pm.brand?.toUpperCase().slice(0, 4)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium">•••• •••• •••• {pm.last4}</span>
                            {pm.is_default && (
                              <Badge className="bg-teal-600">
                                <Star className="h-3 w-3 mr-1" />
                                Default
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            Expires {pm.exp_month}/{pm.exp_year}
                            {pm.funding && <span className="ml-2 capitalize">({pm.funding})</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!pm.is_default && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSetDefault(pm.id)}
                          >
                            Set as Default
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedCard(pm);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Info Tab */}
        <TabsContent value="billing">
          <Card>
            <CardHeader>
              <CardTitle>Billing Information</CardTitle>
              <CardDescription>
                This information will appear on your invoices
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="company_name">Company / Clinic Name</Label>
                  <Input
                    id="company_name"
                    value={billingInfo.company_name || ''}
                    onChange={(e) => setBillingInfo({ ...billingInfo, company_name: e.target.value })}
                    placeholder="Your Clinic Name"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="email">Billing Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={billingInfo.email || ''}
                    onChange={(e) => setBillingInfo({ ...billingInfo, email: e.target.value })}
                    placeholder="billing@clinic.com"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="address_line1">Address Line 1</Label>
                  <Input
                    id="address_line1"
                    value={billingInfo.address_line1 || ''}
                    onChange={(e) => setBillingInfo({ ...billingInfo, address_line1: e.target.value })}
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="address_line2">Address Line 2 (Optional)</Label>
                  <Input
                    id="address_line2"
                    value={billingInfo.address_line2 || ''}
                    onChange={(e) => setBillingInfo({ ...billingInfo, address_line2: e.target.value })}
                    placeholder="Suite 100"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={billingInfo.city || ''}
                    onChange={(e) => setBillingInfo({ ...billingInfo, city: e.target.value })}
                    placeholder="New York"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State / Province</Label>
                  <Input
                    id="state"
                    value={billingInfo.state || ''}
                    onChange={(e) => setBillingInfo({ ...billingInfo, state: e.target.value })}
                    placeholder="NY"
                  />
                </div>
                <div>
                  <Label htmlFor="postal_code">Postal Code</Label>
                  <Input
                    id="postal_code"
                    value={billingInfo.postal_code || ''}
                    onChange={(e) => setBillingInfo({ ...billingInfo, postal_code: e.target.value })}
                    placeholder="10001"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={billingInfo.country || 'US'}
                    onChange={(e) => setBillingInfo({ ...billingInfo, country: e.target.value })}
                    placeholder="US"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="tax_id">Tax ID (Optional)</Label>
                  <Input
                    id="tax_id"
                    value={billingInfo.tax_id || ''}
                    onChange={(e) => setBillingInfo({ ...billingInfo, tax_id: e.target.value })}
                    placeholder="XX-XXXXXXX"
                  />
                </div>
              </div>
              <Separator />
              <div className="flex justify-end">
                <Button onClick={handleSaveBillingInfo} disabled={savingBilling}>
                  {savingBilling ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Save Billing Info
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Payment History</CardTitle>
              <CardDescription>
                View your past payments and download receipts
              </CardDescription>
            </CardHeader>
            <CardContent>
              {paymentHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">No payments yet</p>
                  <p className="text-sm mt-1">Your payment history will appear here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Payment Method</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Receipt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paymentHistory.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell className="font-medium">
                          {formatDate(payment.created_at)}
                        </TableCell>
                        <TableCell>{payment.description}</TableCell>
                        <TableCell>
                          {payment.payment_method_brand && payment.payment_method_last4 ? (
                            <span className="font-mono text-sm">
                              {payment.payment_method_brand} •••• {payment.payment_method_last4}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(payment.amount, payment.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            payment.status === 'succeeded' ? 'bg-green-100 text-green-700' :
                            payment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            payment.status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {payment.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {payment.receipt_url ? (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => window.open(payment.receipt_url, '_blank')}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Receipt
                            </Button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Auto-Renewal Tab */}
        <TabsContent value="auto-renewal">
          <Card>
            <CardHeader>
              <CardTitle>Auto-Renewal Settings</CardTitle>
              <CardDescription>
                Configure automatic subscription renewal
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <RefreshCw className="h-5 w-5 text-gray-500" />
                  <div>
                    <Label className="text-base font-medium">Enable Auto-Renewal</Label>
                    <p className="text-sm text-gray-500">
                      Automatically renew your subscription before it expires
                    </p>
                  </div>
                </div>
                <Switch
                  checked={autoRenewal.enabled}
                  onCheckedChange={(checked) => setAutoRenewal({ ...autoRenewal, enabled: checked })}
                />
              </div>

              {autoRenewal.enabled && (
                <>
                  {/* Payment Method for Renewal */}
                  <div className="space-y-2">
                    <Label>Payment Method for Auto-Renewal</Label>
                    {paymentMethods.length > 0 ? (
                      <div className="flex items-center gap-2 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                        <CreditCard className="h-5 w-5 text-teal-600" />
                        <span>
                          Using default: {paymentMethods.find(pm => pm.is_default)?.brand} •••• {paymentMethods.find(pm => pm.is_default)?.last4}
                        </span>
                      </div>
                    ) : (
                      <Alert className="border-amber-300 bg-amber-50">
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-800">
                          Please add a payment method to enable auto-renewal.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>

                  {/* Notification Settings */}
                  <div className="space-y-2">
                    <Label htmlFor="notify_days">Notify Before Renewal (Days)</Label>
                    <div className="flex items-center gap-4">
                      <Input
                        id="notify_days"
                        type="number"
                        min="1"
                        max="30"
                        value={autoRenewal.notify_days_before}
                        onChange={(e) => setAutoRenewal({ 
                          ...autoRenewal, 
                          notify_days_before: parseInt(e.target.value) || 7 
                        })}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-500">
                        You'll receive a reminder email this many days before renewal
                      </span>
                    </div>
                  </div>

                  {/* Retry Failed Payments */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Shield className="h-5 w-5 text-gray-500" />
                      <div>
                        <Label className="text-base">Retry Failed Payments</Label>
                        <p className="text-sm text-gray-500">
                          Automatically retry if the initial payment fails
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={autoRenewal.retry_failed_payments}
                      onCheckedChange={(checked) => setAutoRenewal({ 
                        ...autoRenewal, 
                        retry_failed_payments: checked 
                      })}
                    />
                  </div>
                </>
              )}

              <Separator />
              <div className="flex justify-end">
                <Button onClick={handleSaveAutoRenewal} disabled={savingAutoRenewal}>
                  {savingAutoRenewal ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Card Dialog */}
      <Dialog open={addCardDialogOpen} onOpenChange={handleCloseAddCardDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
            <DialogDescription>
              Securely add a new card via Stripe
            </DialogDescription>
          </DialogHeader>
          
          {!stripeConfigured ? (
            <Alert className="border-amber-300 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Payment system unavailable. Please contact support for assistance.
              </AlertDescription>
            </Alert>
          ) : clientSecret ? (
            <div className="py-4 space-y-4">
              <Alert className="bg-blue-50 border-blue-200">
                <Shield className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  Your card details are securely processed by Stripe. We never store your full card number.
                </AlertDescription>
              </Alert>
              
              <div className="space-y-2">
                <Label>Card Details</Label>
                <div 
                  ref={cardElementRef}
                  className="p-3 border rounded-md bg-white min-h-[44px]"
                />
                {cardError && (
                  <p className="text-sm text-red-600 mt-1">{cardError}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <div className="mx-auto w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <CreditCard className="h-8 w-8 text-teal-600" />
              </div>
              <p className="text-gray-700 font-medium">Ready to add your card</p>
              <p className="text-sm text-gray-500 mt-1">
                Click "Initialize" to set up secure card entry
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseAddCardDialog}>
              Cancel
            </Button>
            {clientSecret ? (
              <Button onClick={handleConfirmAddCard} disabled={addingCard || !cardComplete}>
                {addingCard ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Save Card
              </Button>
            ) : (
              <Button onClick={handleAddCard} disabled={addingCard || !stripeConfigured}>
                {addingCard ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Initialize
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Payment Method</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this card?
            </DialogDescription>
          </DialogHeader>
          {selectedCard && (
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <CreditCard className="h-8 w-8 text-gray-400" />
              <div>
                <p className="font-medium">{selectedCard.brand} •••• {selectedCard.last4}</p>
                <p className="text-sm text-gray-500">Expires {selectedCard.exp_month}/{selectedCard.exp_year}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteCard}>
              <Trash2 className="h-4 w-4 mr-2" />
              Remove Card
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentMethodsSettings;
